import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { RequestStatus } from '@freelancer/datastore';
import {
  CurrenciesCollection,
  Currency,
  ProjectBudgetOption,
  ProjectBudgetOptionsCollection,
  ProjectUpgradeCardOption,
  ProjectUpgradeFees,
  ProjectUpgradeFeesCollection,
} from '@freelancer/datastore/collections';
import { SelectItem } from '@freelancer/ui/select';
import * as Rx from 'rxjs';
import { BudgetType } from '../../project-budget/budget-type.component';
import { StepName } from '../form.component';

@Component({
  selector: 'app-project',
  template: `
    <fl-bit>
      <app-project-local-question
        *ngIf="showLocalQuestion"
        flTrackingSection="PJPJobType"
        id="{{ StepName.PROJECT_IS_LOCAL }}"
        [control]="formGroup.get(StepName.PROJECT_IS_LOCAL)"
        (cardSelected)="handleProjectLocalCardSelected($event)"
      ></app-project-local-question>
      <app-project-location
        flTrackingSection="PJPLocation"
        id="{{ StepName.PROJECT_LOCATION }}"
        [firstLocationControl]="formGroup.get('location')"
        [secondLocationControl]="formGroup.get('endLocation')"
        [hidden]="!showLocation"
      ></app-project-location>
      <app-project-date
        *ngIf="showProjectDate"
        flTrackingSection="PJPProjectDate"
        id="{{ StepName.PROJECT_DATE }}"
        [control]="formGroup.get(StepName.PROJECT_DATE)"
      ></app-project-date>
      <app-project-equipment
        *ngIf="showProjectEquipment"
        flTrackingSection="PJPProjectEquipment"
        id="{{ StepName.PROJECT_EQUIPMENT }}"
        [control]="formGroup.get(StepName.PROJECT_EQUIPMENT)"
      ></app-project-equipment>
      <app-project-budget-type
        *ngIf="showProjectBudgetType"
        flTrackingSection="PJPProjectType"
        id="{{ StepName.PROJECT_BUDGET_TYPE }}"
        [control]="formGroup.get(StepName.PROJECT_BUDGET_TYPE)"
        (cardSelected)="handleProjectBudgetTypeCardSelected($event)"
      ></app-project-budget-type>
      <app-project-budget
        *ngIf="showProjectBudget"
        flTrackingSection="PJPProjectBudget"
        id="{{ StepName.PROJECT_BUDGET }}"
        [billingCodeControl]="formGroup.get('billingCode')"
        [budgets$]="budgets$"
        [budgetsStatus$]="budgetsStatus$"
        [currenciesStatus$]="currenciesStatus$"
        [currencyIdControl]="formGroup.get('currencyId')"
        [currencyOptions$]="currencyOptions$"
        [customBudgetControl]="formGroup.get('customBudget')"
        [hasBillingCodeControl]="formGroup.get('hasBillingCode')"
        [projectBudgetControl]="formGroup.get(StepName.PROJECT_BUDGET)"
        [selectedCurrency$]="selectedCurrency$"
      ></app-project-budget>
      <app-project-upgrades
        flTrackingSection="PJPProjectUpgrades"
        id="{{ StepName.PROJECT_UPGRADES }}"
        [control]="formGroup.get(StepName.PROJECT_UPGRADES)"
        [hidden]="!showProjectUpgrades"
        [projectTypeControl]="formGroup.get('projectType')"
        [showFreeRecruiterUpgradeBanner]="showFreeRecruiterUpgradeBanner"
        [upgradeFees]="upgradeFees$ | async"
        [upgradeFeesStatus]="upgradeFeesStatus$ | async"
        (advancedUpgradesClicked)="advancedUpgradesClicked.emit($event)"
        (cardSelected)="handleProjectUpgradesCardSelected($event)"
      ></app-project-upgrades>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectComponent {
  StepName = StepName;

  @Input() budgets$: Rx.Observable<ReadonlyArray<ProjectBudgetOption>>;
  @Input() budgetsStatus$: Rx.Observable<
    RequestStatus<ProjectBudgetOptionsCollection>
  >;
  @Input() currenciesStatus$: Rx.Observable<
    RequestStatus<CurrenciesCollection>
  >;
  @Input() currencyOptions$: Rx.Observable<ReadonlyArray<SelectItem>>;
  @Input() formGroup: FormGroup;
  @Input() selectedCurrency$: Rx.Observable<Currency>;
  @Input() showFreeRecruiterUpgradeBanner: boolean;
  @Input() showLocalQuestion: boolean;
  @Input() showLocation: boolean;
  @Input() showProjectBudgetType: boolean;
  @Input() showProjectBudget: boolean;
  @Input() showProjectDate: boolean;
  @Input() showProjectEquipment: boolean;
  @Input() showProjectUpgrades: boolean;
  @Input() upgradeFees$: Rx.Observable<ProjectUpgradeFees>;
  @Input() upgradeFeesStatus$: Rx.Observable<
    RequestStatus<ProjectUpgradeFeesCollection>
  >;

  @Output()
  cardSelected: EventEmitter<{
    stepName: StepName;
    value: boolean | BudgetType | ProjectUpgradeCardOption;
  }> = new EventEmitter();
  @Output() advancedUpgradesClicked: EventEmitter<boolean> = new EventEmitter();

  handleProjectLocalCardSelected(value: boolean) {
    this.cardSelected.emit({ stepName: StepName.PROJECT_IS_LOCAL, value });
  }

  handleProjectBudgetTypeCardSelected(value: BudgetType) {
    this.cardSelected.emit({ stepName: StepName.PROJECT_BUDGET_TYPE, value });
  }

  handleProjectUpgradesCardSelected(value: ProjectUpgradeCardOption) {
    this.cardSelected.emit({ stepName: StepName.PROJECT_UPGRADES, value });
  }
}
